import { Link, NavLink, useLocation } from 'react-router-dom';
import {
  Menu,
  X,
  Moon,
  Sun,
  HelpCircle,
  ChevronRight,
  ChevronDown,
  Home,
  BookOpen,
  Info,
  Users,
  Calendar,
  Mail,
  Heart,
} from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { useTheme } from '../../contexts/ThemeContext';
import LanguageSwitcher from './LanguageSwitcher';
import UserGuideModal from '../features/UserGuideModal';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activitiesOpen, setActivitiesOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const activitiesRef = useRef(null);
  const contactRef = useRef(null);
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const prefersReducedMotion = useReducedMotion();

  const { t } = useTranslation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 16);
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setActivitiesOpen(false);
    setContactOpen(false);
  }, [location.pathname]);

  // Lock body scroll and handle escape key when mobile drawer is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
      const handleEscape = (e) => {
        if (e.key === 'Escape') setMobileMenuOpen(false);
      };
      window.addEventListener('keydown', handleEscape);
      return () => {
        document.body.style.overflow = 'unset';
        window.removeEventListener('keydown', handleEscape);
      };
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [mobileMenuOpen]);

  const mainNav = [
    { name: t('nav.home'), href: '/', icon: Home },
    { name: t('nav.books'), href: '/books', icon: BookOpen },
    { name: t('nav.about'), href: '/about', icon: Info },
  ];

  const activitiesItems = [
    { name: t('nav.groups'), href: '/groups', icon: Users },
    { name: t('nav.events'), href: '/events', icon: Calendar },
  ];

  const contactItems = [
    { name: t('nav.contact'), href: '/contact', icon: Mail },
    { name: t('community.title'), href: '/community', icon: Heart },
  ];

  const isActive = (href) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  const isDropdownActive = (items) =>
    items.some((item) => isActive(item.href));

  return (
    <>
      <header
        className={`sticky top-0 z-40 transition-all duration-200 ${
          scrolled
            ? 'bg-white/95 dark:bg-dark-surface/95 backdrop-blur-md shadow-[0_4px_20px_rgba(46,139,87,0.10)] border-b border-gray-200/80 dark:border-gray-800'
            : 'bg-white/85 dark:bg-dark-surface/85 backdrop-blur-md border-b border-gray-100 dark:border-gray-800/80'
        }`}
      >
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14 sm:h-16">
            {/* Logo and Brand Title */}
            <div className="flex items-center min-w-0">
              <Link to="/" className="flex items-center space-x-2.5 group">
                <img
                  src="/assets/logo/logo-2.jpeg"
                  alt={t('common.logo_alt')}
                  width="40"
                  height="40"
                  className="h-9 w-9 sm:h-10 sm:w-10 rounded-full object-cover shadow-sm ring-1 ring-primary-500/20 group-hover:scale-105 transition-transform"
                />
                <span className="text-base sm:text-lg lg:text-xl font-bold font-display text-gray-900 dark:text-white tracking-tight truncate">
                  PROTEGE QV Lecture+
                </span>
              </Link>
            </div>

            {/* Desktop Navigation (>= md) */}
            <div className="hidden md:flex items-center space-x-1 lg:space-x-2">
              {/* Home */}
              <NavLink
                to="/"
                className={({ isActive: active }) =>
                  `relative px-3 py-2 text-sm font-medium transition-colors ${
                    active
                      ? 'text-primary-600 dark:text-primary-400 font-semibold'
                      : 'text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400'
                  }`
                }
              >
                {({ isActive: active }) => (
                  <>
                    {t('nav.home')}
                    {active && (
                      <motion.div
                        layoutId="header-underline"
                        className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-600 dark:bg-primary-400 rounded-full"
                        transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                      />
                    )}
                  </>
                )}
              </NavLink>

              {/* Books */}
              <NavLink
                to="/books"
                className={({ isActive: active }) =>
                  `relative px-3 py-2 text-sm font-medium transition-colors ${
                    active
                      ? 'text-primary-600 dark:text-primary-400 font-semibold'
                      : 'text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400'
                  }`
                }
              >
                {({ isActive: active }) => (
                  <>
                    {t('nav.books')}
                    {active && (
                      <motion.div
                        layoutId="header-underline"
                        className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-600 dark:bg-primary-400 rounded-full"
                        transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                      />
                    )}
                  </>
                )}
              </NavLink>

              {/* Dropdown: Groupes & Événements */}
              <div
                ref={activitiesRef}
                className="relative"
                onMouseEnter={() => setActivitiesOpen(true)}
                onMouseLeave={() => setActivitiesOpen(false)}
              >
                <button
                  type="button"
                  className={`flex items-center gap-1 px-3 py-2 text-sm font-medium transition-colors rounded-lg ${
                    isDropdownActive(activitiesItems)
                      ? 'text-primary-600 dark:text-primary-400 font-semibold'
                      : 'text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400'
                  }`}
                  aria-expanded={activitiesOpen}
                  aria-haspopup="true"
                >
                  <span>{t('nav.dropdown_activities')}</span>
                  <ChevronDown
                    className={`h-4 w-4 transition-transform duration-200 ${
                      activitiesOpen ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                <AnimatePresence>
                  {activitiesOpen && (
                    <motion.div
                      initial={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: -6, scale: 0.96 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: -6, scale: 0.96 }}
                      transition={{ duration: 0.15 }}
                      className="absolute left-0 top-full pt-1 z-30"
                    >
                      <div className="bg-white dark:bg-dark-surface rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 py-1.5 min-w-[200px] overflow-hidden">
                        {activitiesItems.map((item) => {
                          const Icon = item.icon;
                          const active = isActive(item.href);
                          return (
                            <Link
                              key={item.href}
                              to={item.href}
                              className={`flex items-center gap-2.5 px-4 py-2.5 text-sm transition-colors ${
                                active
                                  ? 'text-primary-700 dark:text-primary-300 bg-primary-50 dark:bg-primary-900/20 font-semibold'
                                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                              }`}
                            >
                              <Icon className={`h-4 w-4 ${active ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400'}`} />
                              <span>{item.name}</span>
                            </Link>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* About */}
              <NavLink
                to="/about"
                className={({ isActive: active }) =>
                  `relative px-3 py-2 text-sm font-medium transition-colors ${
                    active
                      ? 'text-primary-600 dark:text-primary-400 font-semibold'
                      : 'text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400'
                  }`
                }
              >
                {({ isActive: active }) => (
                  <>
                    {t('nav.about')}
                    {active && (
                      <motion.div
                        layoutId="header-underline"
                        className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-600 dark:bg-primary-400 rounded-full"
                        transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                      />
                    )}
                  </>
                )}
              </NavLink>

              {/* Dropdown: Contact & Communauté */}
              <div
                ref={contactRef}
                className="relative"
                onMouseEnter={() => setContactOpen(true)}
                onMouseLeave={() => setContactOpen(false)}
              >
                <button
                  type="button"
                  className={`flex items-center gap-1 px-3 py-2 text-sm font-medium transition-colors rounded-lg ${
                    isDropdownActive(contactItems)
                      ? 'text-primary-600 dark:text-primary-400 font-semibold'
                      : 'text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400'
                  }`}
                  aria-expanded={contactOpen}
                  aria-haspopup="true"
                >
                  <span>{t('nav.dropdown_contact')}</span>
                  <ChevronDown
                    className={`h-4 w-4 transition-transform duration-200 ${
                      contactOpen ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                <AnimatePresence>
                  {contactOpen && (
                    <motion.div
                      initial={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: -6, scale: 0.96 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: -6, scale: 0.96 }}
                      transition={{ duration: 0.15 }}
                      className="absolute left-0 top-full pt-1 z-30"
                    >
                      <div className="bg-white dark:bg-dark-surface rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 py-1.5 min-w-[210px] overflow-hidden">
                        {contactItems.map((item) => {
                          const Icon = item.icon;
                          const active = isActive(item.href);
                          return (
                            <Link
                              key={item.href}
                              to={item.href}
                              className={`flex items-center gap-2.5 px-4 py-2.5 text-sm transition-colors ${
                                active
                                  ? 'text-primary-700 dark:text-primary-300 bg-primary-50 dark:bg-primary-900/20 font-semibold'
                                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                              }`}
                            >
                              <Icon className={`h-4 w-4 ${active ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400'}`} />
                              <span>{item.name}</span>
                            </Link>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Divider */}
              <div className="h-5 w-px bg-gray-200 dark:bg-gray-700 mx-1" />

              {/* Join CTA */}
              <motion.div
                whileHover={prefersReducedMotion ? {} : { scale: 1.03 }}
                whileTap={prefersReducedMotion ? {} : { scale: 0.97 }}
              >
                <Link
                  to="/community"
                  className="inline-flex items-center px-3.5 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                >
                  {t('nav.join_library')}
                </Link>
              </motion.div>

              {/* Language Switcher */}
              <LanguageSwitcher />

              {/* Theme Toggle */}
              <motion.button
                whileHover={prefersReducedMotion ? {} : { scale: 1.05 }}
                whileTap={prefersReducedMotion ? {} : { scale: 0.95 }}
                onClick={toggleTheme}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-300 transition-colors"
                aria-label={t('common.toggle_theme')}
                title={t('common.toggle_theme')}
              >
                {theme === 'light' ? (
                  <Moon className="h-5 w-5" />
                ) : (
                  <Sun className="h-5 w-5" />
                )}
              </motion.button>

              {/* User Guide */}
              <motion.button
                whileHover={prefersReducedMotion ? {} : { scale: 1.05 }}
                whileTap={prefersReducedMotion ? {} : { scale: 0.95 }}
                onClick={() => setShowGuide(true)}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-primary-600 dark:text-primary-400 transition-colors"
                aria-label={t('user_guide.title')}
                title={t('user_guide.title')}
              >
                <HelpCircle className="h-5 w-5" />
              </motion.button>
            </div>

            {/* Mobile Top Header Controls (< md, decluttered for <=390px viewports) */}
            <div className="md:hidden flex items-center space-x-1 sm:space-x-1.5">
              <motion.button
                whileHover={prefersReducedMotion ? {} : { scale: 1.05 }}
                whileTap={prefersReducedMotion ? {} : { scale: 0.95 }}
                onClick={() => setShowGuide(true)}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-primary-600 dark:text-primary-400 transition-colors"
                aria-label={t('user_guide.title')}
                title={t('user_guide.title')}
              >
                <HelpCircle className="h-5 w-5" />
              </motion.button>

              <motion.button
                whileHover={prefersReducedMotion ? {} : { scale: 1.05 }}
                whileTap={prefersReducedMotion ? {} : { scale: 0.95 }}
                onClick={toggleTheme}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-300 transition-colors"
                aria-label={t('common.toggle_theme')}
                title={t('common.toggle_theme')}
              >
                {theme === 'light' ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
              </motion.button>

              <motion.button
                whileHover={prefersReducedMotion ? {} : { scale: 1.05 }}
                whileTap={prefersReducedMotion ? {} : { scale: 0.95 }}
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200 transition-colors"
                aria-label={mobileMenuOpen ? t('common.close_menu') : t('common.open_menu')}
              >
                {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </motion.button>
            </div>
          </div>
        </nav>
      </header>

      {/* Mobile Slide-Over Navigation Drawer */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/50 z-40 md:hidden"
              onClick={() => setMobileMenuOpen(false)}
              aria-hidden="true"
            />

            {/* Slide-over Drawer Panel */}
            <motion.div
              initial={prefersReducedMotion ? { opacity: 0 } : { x: '100%' }}
              animate={prefersReducedMotion ? { opacity: 1 } : { x: 0 }}
              exit={prefersReducedMotion ? { opacity: 0 } : { x: '100%' }}
              transition={{ type: 'spring', damping: 28, stiffness: 280 }}
              className="fixed top-0 right-0 bottom-0 w-72 max-w-[85vw] bg-white dark:bg-dark-surface shadow-xl z-50 md:hidden flex flex-col border-l border-gray-200 dark:border-gray-700"
              role="dialog"
              aria-modal="true"
              aria-label={t('nav.menu')}
            >
              {/* Drawer Header */}
              <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center bg-gray-50/50 dark:bg-gray-800/30">
                <div className="flex items-center space-x-2">
                  <img
                    src="/assets/logo/logo-2.jpeg"
                    alt={t('common.logo_alt')}
                    width="30"
                    height="30"
                    className="h-7 w-7 rounded-full object-cover ring-1 ring-primary-500/20"
                  />
                  <span className="font-bold text-gray-900 dark:text-white text-base">
                    {t('nav.menu')}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setMobileMenuOpen(false)}
                  className="p-1.5 rounded-lg hover:bg-gray-200/70 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 transition-colors"
                  aria-label={t('common.close_menu')}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Drawer Content */}
              <div className="flex-1 overflow-y-auto py-3 px-3 space-y-3">
                {/* Primary Nav Items */}
                <div className="space-y-1">
                  {mainNav.map((item) => {
                    const Icon = item.icon;
                    const active = isActive(item.href);
                    return (
                      <Link
                        key={item.href}
                        to={item.href}
                        className={`flex items-center justify-between px-3.5 py-2.5 rounded-xl text-sm font-medium transition-all ${
                          active
                            ? 'text-primary-700 dark:text-primary-300 bg-primary-50 dark:bg-primary-900/20 font-semibold'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100/70 dark:hover:bg-gray-800/60'
                        }`}
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <div className="flex items-center space-x-3">
                          <Icon className={`h-4 w-4 ${active ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400 dark:text-gray-500'}`} />
                          <span>{item.name}</span>
                        </div>
                        <ChevronRight className={`h-4 w-4 ${active ? 'text-primary-500' : 'text-gray-300 dark:text-gray-600'}`} />
                      </Link>
                    );
                  })}
                </div>

                {/* Section: Activities */}
                <div className="pt-2">
                  <div className="px-3 text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                    {t('nav.dropdown_activities')}
                  </div>
                  <div className="mt-1 space-y-1">
                    {activitiesItems.map((item) => {
                      const Icon = item.icon;
                      const active = isActive(item.href);
                      return (
                        <Link
                          key={item.href}
                          to={item.href}
                          className={`flex items-center justify-between px-3.5 py-2 rounded-xl text-sm transition-all ${
                            active
                              ? 'text-primary-700 dark:text-primary-300 bg-primary-50 dark:bg-primary-900/20 font-semibold'
                              : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100/70 dark:hover:bg-gray-800/60'
                          }`}
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          <div className="flex items-center space-x-3">
                            <Icon className={`h-4 w-4 ${active ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400 dark:text-gray-500'}`} />
                            <span>{item.name}</span>
                          </div>
                          <ChevronRight className={`h-4 w-4 ${active ? 'text-primary-500' : 'text-gray-300 dark:text-gray-600'}`} />
                        </Link>
                      );
                    })}
                  </div>
                </div>

                {/* Section: Contact & Community */}
                <div className="pt-2">
                  <div className="px-3 text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                    {t('nav.dropdown_contact')}
                  </div>
                  <div className="mt-1 space-y-1">
                    {contactItems.map((item) => {
                      const Icon = item.icon;
                      const active = isActive(item.href);
                      return (
                        <Link
                          key={item.href}
                          to={item.href}
                          className={`flex items-center justify-between px-3.5 py-2 rounded-xl text-sm transition-all ${
                            active
                              ? 'text-primary-700 dark:text-primary-300 bg-primary-50 dark:bg-primary-900/20 font-semibold'
                              : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100/70 dark:hover:bg-gray-800/60'
                          }`}
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          <div className="flex items-center space-x-3">
                            <Icon className={`h-4 w-4 ${active ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400 dark:text-gray-500'}`} />
                            <span>{item.name}</span>
                          </div>
                          <ChevronRight className={`h-4 w-4 ${active ? 'text-primary-500' : 'text-gray-300 dark:text-gray-600'}`} />
                        </Link>
                      );
                    })}
                  </div>
                </div>

                {/* Section: Language Switcher in Drawer */}
                <div className="pt-3 border-t border-gray-100 dark:border-gray-800">
                  <div className="px-3 py-2 flex items-center justify-between bg-gray-50 dark:bg-gray-800/40 rounded-xl">
                    <span className="text-xs font-medium text-gray-600 dark:text-gray-300">
                      {t('common.change_language')}
                    </span>
                    <LanguageSwitcher />
                  </div>
                </div>
              </div>

              {/* Drawer Footer CTA */}
              <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/30">
                <Link
                  to="/community"
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-primary-600 hover:bg-primary-700 active:bg-primary-800 text-white text-sm font-semibold rounded-xl shadow-md shadow-primary-600/20 transition-all"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <span>{t('nav.join_library')}</span>
                  <ChevronRight className="h-4 w-4" />
                </Link>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Accessible User Guide Modal */}
      <UserGuideModal isOpen={showGuide} onClose={() => setShowGuide(false)} />
    </>
  );
};

export default Header;
